/**
 * "My grades" — one course, this learner's own standing in it.
 *
 * READS `GET /api/awards/v1/grades/me/<course_key>`, which answers in one of three
 * shapes (`grading: 'graded' | 'ungraded' | 'misconfigured'`), and the shape is what this
 * page is built around — see `shared/lib/grades.ts` for why that is a discriminated union
 * rather than one interface with optional fields.
 *
 * THREE RULES THIS PAGE EXISTS TO ENFORCE, each one a way the obvious version lies:
 *
 *   GRADED          show the percent, the pass mark, the per-assignment breakdown, and how
 *                   far short of the pass mark this learner is when they are short. A
 *                   `percent` or `pass_cutoff_percent` of `null` is UNGRADED CONTENT, not a
 *                   zero — printing `0%` for "not yet attempted" tells a learner who has not
 *                   started that they have failed.
 *
 *   UNGRADED        NO percentage anywhere on this branch, not even one computed from unit
 *                   completion. The server sends no `percent` and no `pass_cutoff_percent`
 *                   for exactly this reason — a course with no graded work has no score to
 *                   report, and a client-side stand-in (e.g. "50% of units") would answer a
 *                   question ("what is my grade") the course was never going to ask. What it
 *                   DOES have is completion, reported as units, never a percentage.
 *
 *   MISCONFIGURED   this is the ACADEMY'S problem, stated as one. `max_achievable_percent`
 *                   and `pass_cutoff_percent` here describe a broken POLICY (weights that do
 *                   not add to 100, or graders with nothing behind them) — they are not this
 *                   learner's score, and the copy never says "you did not pass" or shows a
 *                   personal number, because a learner cannot fail a scale that cannot be
 *                   completed by anyone.
 *
 * VOCABULARY IS DELIBERATE. "Completed" is the only verb the ungraded branch uses for
 * progress — never "passed", "achieved" or "scored", none of which are true of a course
 * that grades nothing.
 */

import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Banner, Icon, Spinner } from '../../shared/ui';
import { loadSession } from '../../shared/lib/session';
import { courseHref, getCourse, NotOurCourse } from '../../shared/lib/catalogue';
import { ApiError } from '../../shared/lib/openedx';
import {
  explainGradeFailure,
  getMyGrade,
  type GradeAssignment,
  type GradedGrade,
  type MisconfiguredGrade,
  type MyGrade,
  type UngradedGrade,
} from '../../shared/lib/grades';
import './my-grades.css';

/** Floats arrive with long tails; every screen in this app prints them the same way. */
function pct(value: number): string {
  return Number.isInteger(value) ? String(value) : String(Math.round(value * 100) / 100);
}

export default function MyGrades() {
  const { courseKey = '' } = useParams();

  const session = useQuery({ queryKey: ['session'], queryFn: loadSession, staleTime: 5 * 60_000 });
  const username = session.data?.user?.username ?? null;

  // Same query key CourseHome uses for this course, so arriving here from that page costs
  // nothing extra — react-query serves the cached record.
  const course = useQuery({
    queryKey: ['course', courseKey],
    queryFn: () => getCourse(courseKey),
    retry: false,
  });

  const grade = useQuery({
    queryKey: ['my-grade', courseKey],
    queryFn: () => getMyGrade(courseKey),
    enabled: Boolean(username),
    retry: false,
  });

  if (session.isLoading) {
    return (
      <main className="my-grades">
        <Spinner label="Loading…" />
      </main>
    );
  }

  if (!username) {
    return (
      <main className="my-grades">
        <Banner tone="info">
          <strong>Sign in to see your grades.</strong> They are tied to your account, not to
          this browser.
        </Banner>
      </main>
    );
  }

  const backHref = courseHref(courseKey);
  const notFound =
    course.isError &&
    ((course.error instanceof ApiError && [400, 404].includes(course.error.status)) ||
      course.error instanceof NotOurCourse);

  return (
    <main className="my-grades">
      <div className="my-grades__head">
        <div>
          <h1>My grades</h1>
          <p>{course.data?.name ?? 'This course'}</p>
        </div>
        <Link className="ui-btn" data-variant="secondary" to={backHref}>
          <Icon name="arrow_back" />
          Back to course
        </Link>
      </div>

      {notFound && (
        <Banner tone="danger">
          <strong>This course could not be found.</strong> The link may be wrong, or the
          course may no longer be part of this academy.
        </Banner>
      )}

      {grade.isPending && (
        <div className="my-grades__loading">
          <Spinner label="Loading your grade…" />
        </div>
      )}

      {grade.isError && (
        <Banner tone="danger">
          <strong>Your grade could not be loaded.</strong> {explainGradeFailure(grade.error)}
        </Banner>
      )}

      {grade.data?.grading === 'graded' && <GradedView grade={grade.data} />}
      {grade.data?.grading === 'ungraded' && <UngradedView grade={grade.data} />}
      {grade.data?.grading === 'misconfigured' && <MisconfiguredView grade={grade.data} />}
    </main>
  );
}

/* ------------------------------------------------------------------ completion ----- */

/**
 * Units complete, in units — never a percentage. Shared by all three branches because the
 * fields are always present, but it is the ONLY progress fact the ungraded branch is allowed
 * to show, which is why it lives outside any one of the three views below.
 */
function CompletionLine({ grade }: { grade: MyGrade }) {
  const total = grade.units_complete + grade.units_incomplete + grade.units_locked;
  return (
    <p className="my-grades__completion">
      <Icon name={grade.complete ? 'check_circle' : 'donut_large'} />
      {grade.units_complete} of {total} {total === 1 ? 'unit' : 'units'} complete
      {grade.units_locked > 0 &&
        ` (${grade.units_locked} locked — finish earlier content to unlock ${grade.units_locked === 1 ? 'it' : 'them'})`}
    </p>
  );
}

/* ---------------------------------------------------------------------- graded ----- */

function GradedView({ grade }: { grade: GradedGrade }) {
  return (
    <>
      <section className="my-grades__summary">
        <div className="my-grades__score">
          <span className="my-grades__pct">
            {grade.percent === null ? 'Not yet graded' : `${pct(grade.percent)}%`}
          </span>
          {grade.pass_cutoff_percent !== null && (
            <span className="my-grades__cutoff">Pass mark {pct(grade.pass_cutoff_percent)}%</span>
          )}
        </div>

        {grade.passed ? (
          <Banner tone="success">
            <strong>You have passed this course.</strong>
            {grade.letter_grade && ` Grade: ${grade.letter_grade}.`}
          </Banner>
        ) : (
          grade.shortfall_percent !== null && (
            <Banner tone="warning">
              You are {pct(grade.shortfall_percent)} points short of the pass mark.
            </Banner>
          )
        )}

        <CompletionLine grade={grade} />
      </section>

      {grade.assignments.length > 0 && (
        <section className="my-grades__breakdown">
          <h2>Assignments</h2>
          <div className="my-grades__table">
            {grade.assignments.map((assignment, index) => (
              <AssignmentRow key={`${assignment.assignment_type}-${index}`} assignment={assignment} />
            ))}
          </div>
        </section>
      )}
    </>
  );
}

function AssignmentRow({ assignment }: { assignment: GradeAssignment }) {
  return (
    <div className="my-grades__row" data-attempted={assignment.attempted}>
      <div className="my-grades__row-title">
        <span>{assignment.title}</span>
        <span className="my-grades__row-type">
          {assignment.assignment_type}
          {assignment.type_weight_percent !== null &&
            ` · weighted ${pct(assignment.type_weight_percent)}%`}
        </span>
      </div>
      <div className="my-grades__row-score">
        {assignment.attempted ? `${assignment.earned} / ${assignment.possible}` : 'Not attempted'}
      </div>
      <div className="my-grades__row-pct">
        {assignment.percent === null ? 'Not yet graded' : `${pct(assignment.percent)}%`}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- ungraded ----- */

/**
 * NO PERCENTAGE ANYWHERE IN THIS FUNCTION, on purpose — grep the rendered output for '%'
 * and it must find nothing. `CompletionLine` reports units, never a percentage, and the
 * server's own `message` is rendered as given rather than paraphrased into one.
 */
function UngradedView({ grade }: { grade: UngradedGrade }) {
  return (
    <section className="my-grades__summary">
      <Banner tone="info">{grade.message}</Banner>
      <CompletionLine grade={grade} />
      <p className="my-grades__note">
        This course has no graded work, so there is no score to report. The certificate
        follows completion, not a grade.
      </p>
    </section>
  );
}

/* ---------------------------------------------------------------- misconfigured ----- */

/**
 * THE ACADEMY'S PROBLEM, STATED AS ONE. Neither number here is this learner's — both
 * describe a policy ceiling nobody in the course can get past — so the copy never says
 * "you failed" and never shows a personal score.
 */
function MisconfiguredView({ grade }: { grade: MisconfiguredGrade }) {
  return (
    <section className="my-grades__summary">
      <Banner tone="warning">
        <strong>This course's grading is not set up correctly.</strong> {grade.message} The
        highest grade this course can award is {pct(grade.max_achievable_percent)}%, and its
        pass mark is {pct(grade.pass_cutoff_percent)}%. This is a setup issue for the academy
        to fix — it is not something you did, and not something you can pass or fail as the
        policy stands.
      </Banner>
      <CompletionLine grade={grade} />
    </section>
  );
}
